import { Recurrence, SelfServiceSchedulePaneProps, StaticInitializers } from '@tableau/self-service-schedules';
import { HostedStackedElementContainer } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';
import { CreateExtractRefreshAction } from '~src/app/actions/spaces/CreateExtractRefreshAction';
import { getAppStore } from '~src/app/AppStore';
import { OpenRefreshSelfServiceScheduleDialogParameters } from '~src/app/content/RefreshSelfServiceScheduleDialog';
import { selectContentTotalLength } from '~src/app/selectors/ContentSelectors';
import { SessionServerSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { ServiceLocator } from '~src/app/ServiceLocator';
import { hasRunNowPermission } from '~src/app/utils/MixedContentHelpers';
import { Messages } from '~src/codegen/Localize';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg } from '~src/commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '~src/commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '~src/commonInfra/contentActions/MultipleItemResult';
import { toNativePromise } from '~src/commonInfra/utils/QUtils';
import { SearchSuggestionsService } from '~src/content/services/SearchSuggestionsService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { openCreateExtractTaskActionDialog, PropsForCreateExtractTaskActionBody } from '~src/schedule/components/CreateExtractTaskActionBody';
import { ISchedule, ScheduleActions } from '~src/schedule/types/ISchedule';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { IActionContext } from '~src/ts/helpers/ActionContext';
import { SchedulingErrorCode, WebClientErrorCode } from '~src/ts/helpers/ErrorCodes';
import { RefreshPropertyHelper } from '~src/ts/helpers/RefreshPropertyHelper';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { IDataSourceBase, IDataSourceDetailInfo } from '~src/ts/models/IDataSourceInfo';
import { IRemoteRefreshScheduleInfo } from '~src/ts/models/IRemoteRefreshScheduleInfo';
import { ITypeaheadSource } from '~src/ts/models/ITypeaheadSource';
import { IWorkbookBase } from '~src/ts/models/IWorkbookInfo';
import { IExtractItemInfo } from '~src/ts/models/resourceInfoParts';
import { Set } from '~src/ts/models/Set';
import { CreateExtractTasksSelfServiceSchedulesRequest, RefreshTargetType, RefreshTaskType } from '~src/ts/ServerApi/requests/CreateSelfServiceScheduleExtractTasksRequest';
import { RemoteRefreshTypes } from '~src/ts/ServerApi/requests/GetRemoteRefreshSchedulesRequest';
import { ExtractTaskType } from '~src/ts/ServerApi/types/IExtractTask';
import { RefreshModeTypes } from '~src/ts/ServerApi/types/IRemoteRefreshProperties';
import { SelfServiceSchedule } from '~src/ts/ServerApi/types/SelfServiceSchedule';
import { IRemoteRefreshAgentOption, IRemoteRefreshPropertiesEditModel } from '~src/ts/services/ChangeDatasourceRemoteRefreshPropertiesHelper';
import { ChangeScheduleActionService } from '~src/ts/services/ChangeScheduleAction';
import { ConfirmActionDialog, IConfirmActionDialogButton } from '~src/ts/services/ConfirmActionDialog';
import { ModalSize } from '~src/ts/services/ModalService';
import { CreateBridgeExtractTaskActionService } from './CreateBridgeExtractTaskAction';

export enum ExtractActionType {
  createExtracts = 'CreateExtracts',
  encryptExtract = 'EncryptExtract',
  decryptExtract = 'DecryptExtract'
}

export enum CreateExtractTasksMode {
  AllowRefreshNow,
  AllowScheduleOnServer,
  AllowScheduleOnServerAndClient,
  AllowRefreshNowAndOnServer,
  AllowRefreshNowAndOnServerAndClient,
  AllowScheduleOnlyOnClient
}

export interface IExtractTaskTypeOption {
  labelTranslateKey: string;
  value: string;
  tbTestId: string;
}

export interface IRemoteRefreshTypeOption {
  refreshType: string;
}

export interface IExtractTaskSchedule {
  schedule?: ISchedule;
  type?: string;
}

export interface ICreateExtractTasksScope extends ng.IScope {
  dialog: {
    mode: string;   // See DialogMode
    showModeSelector: boolean;
    showRefreshNowTab: boolean;
    showRefreshOnClientTab: boolean;
  };

  extractTask: IExtractTaskSchedule;

  scheduleOnServerPrompt: string;
  scheduleOnClientPrompt: string;
  scheduleSource: ITypeaheadSource;

  refreshNowPrompt: string;

  allowIncremental: boolean;
  extractTaskTypeOptions?: IExtractTaskTypeOption[];

  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;

  // The followings are only for shedules to be run on Bridge Clients
  selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps;
  remoteRefreshPropertiesEditModel: IRemoteRefreshPropertiesEditModel;
  existingRemoteRefreshPropertiesEditModel: IRemoteRefreshPropertiesEditModel;
  remoteAgentOptions: IRemoteRefreshAgentOption[];
  remoteAvailableTypeOptions: string[];
  remoteRefreshTypeOption: IRemoteRefreshTypeOption;

  existingRefreshSchedulesCount: Number;
}

export type ExtractType = IWorkbookBase | IDataSourceBase;

export namespace CreateExtractTaskActionService {
  export function dialogTitle(allowedMode: CreateExtractTasksMode) {
    switch (allowedMode) {
      case CreateExtractTasksMode.AllowRefreshNowAndOnServer:
      case CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient:
        return Messages.extractTasksDialogTitle_AllowBoth();
      case CreateExtractTasksMode.AllowScheduleOnlyOnClient:
        return Messages.extractTasksDialogTitle_ScheduleLegacyBridgeRefresh();
      case CreateExtractTasksMode.AllowRefreshNow:
        return Messages.extractTasksDialogTitle_AllowRefreshNow();
      case CreateExtractTasksMode.AllowScheduleOnServer:
      case CreateExtractTasksMode.AllowScheduleOnServerAndClient:
        return Messages.extractTasksDialogTitle_AllowSchedule();
      default:
        throw 'Unknown allowed mode: ' + allowedMode;
    }
  }

  export function extractErrorMessageFor(errorItem: IErrorItem<IExtractItemInfo>, schedule: ISchedule) {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingScheduleNotFound:
        return Messages.scheduleNotFound({ SCHEDULE: schedule.name });
      case WebClientErrorCode.NotSupportedForRemotelyRefreshedContent:
        return Messages.refreshNowRemotelyNotSupported();
    }
    return Messages.errorUnknown();
  }

  export function getRefreshSSSDialogParams(targetResources: (IDataSourceBase | IWorkbookBase)[]): OpenRefreshSelfServiceScheduleDialogParameters {
    // Bridge Related
    const existingRefreshSchedulesCount = selectContentTotalLength(getAppStore().getState());
    const datasources = targetResources.filter(c => c.contentType === 'datasource');
    const showSchedulesWillBeDeletedWarning = existingRefreshSchedulesCount > 0 && RefreshPropertyHelper.allDatasourcesHaveLegacyBridgeSchedules(datasources as IDataSourceDetailInfo[]);

    // Target Type
    const capitalizeString = (s: string) => s[0].toUpperCase() + s.substring(1);
    const targetTypes = targetResources.map(content => content.contentType).map(capitalizeString);
    const allSameTargetType = _.unique(targetTypes).length === 1;
    const targetType = allSameTargetType
      ? targetTypes[0] as RefreshTargetType
      : 'Mixed';

    // Task Type
    const canSupportIncremental = targetResources.map(target => target.hasIncrementalExtract);
    const allowIncremental = _.all(canSupportIncremental);

    const quantity = targetResources.length;
    const name = quantity === 1
      ? targetResources[0].name
      : undefined;
    const save = (recurrence: Recurrence, taskType: RefreshTaskType, scheduleName: string): Promise<IResult> => {
      const request = new CreateExtractTasksSelfServiceSchedulesRequest({
        schedule: SelfServiceSchedule.fromRecurrence(recurrence, 'Extract'),
        scheduleName: scheduleName.substring(0, 255),
        targetIds: targetResources.map(content => Number(content.id)),
        targetType,
        type: taskType,
      });
      return new Promise<IResult>((resolve, reject) => {
        WebHttpClientWithErrorToastInstance.sendRequest(request).then(result => {
          CreateExtractRefreshAction.notifyResult(result, true);
          resolve(result);
        }).catch(err => {
          CreateExtractRefreshAction.notifyResult();
          reject(err);
        });
      });
    };
    return {
      quantity, targetType, name,
      save: save,
      isNew: true,
      taskType: allowIncremental ? 'IncrementExtract' : 'RefreshExtract',
      disableSelectingTaskType: !allowIncremental,
      showExistingTasksWillBeDeletedWarning: showSchedulesWillBeDeletedWarning,
    };
  }


  const DialogMode = {
    RefreshNow: 'RefreshNow',
    ScheduleRefreshOnServer: 'ScheduleRefreshOnServer',
    ScheduleRefreshOnClient: 'ScheduleRefreshOnClient'
  };

  export function populateScope(scope: ICreateExtractTasksScope,
    items: Set<IExtractItemInfo>,
    scheduleOnServerPrompt: string,
    scheduleOnClientPrompt: string,
    allowedMode: CreateExtractTasksMode,
    refreshNowPrompt: string
  ) {
    scope.extractTask = {
      type: ExtractTaskType.RefreshExtract
    };
    scope.remoteRefreshTypeOption = {
      refreshType: RemoteRefreshTypes.Full
    };

    scope.scheduleOnServerPrompt = scheduleOnServerPrompt;
    scope.scheduleOnClientPrompt = scheduleOnClientPrompt;
    scope.scheduleSource = SearchSuggestionsService.schedulesSource(ScheduleActions.Extract);

    scope.allowIncremental = _.any(items.toArray(), (item: IExtractItemInfo) => item.hasIncrementalExtract);
    if (scope.allowIncremental) {
      scope.extractTaskTypeOptions = [
        {
          value: ExtractTaskType.RefreshExtract,
          labelTranslateKey: 'extractTasksTypeRefreshExtract',
          tbTestId: 'create-extract-refresh-type-full'
        },
        {
          value: ExtractTaskType.IncrementExtract,
          labelTranslateKey: 'extractTasksTypeIncrementExtract',
          tbTestId: 'create-extract-refresh-type-incremental'
        }
      ];
      scope.remoteAvailableTypeOptions = [
        RemoteRefreshTypes.Full,
        RemoteRefreshTypes.Incremental
      ];
    }

    scope.dialog = {
      mode: (allowedMode === CreateExtractTasksMode.AllowScheduleOnServer ||
        allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient)
        ? DialogMode.ScheduleRefreshOnServer : DialogMode.RefreshNow,
      showModeSelector: (allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient ||
        allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServer ||
        allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient),
      showRefreshNowTab: (allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServer ||
        allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient),
      showRefreshOnClientTab: false
    };

    scope.refreshNowPrompt = refreshNowPrompt;

    scope.remoteRefreshPropertiesEditModel = { refreshMode: RefreshModeTypes.Server, remoteAgentName: 'dummy' };

    scope.existingRefreshSchedulesCount = selectContentTotalLength(getAppStore().getState());

    // Set up remote refresh info when the mode is allowed.
    if ((allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient ||
      allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient ||
      allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient)
      && items.size() === 1) {
      const datasourceDetailInfo: IDataSourceDetailInfo = items.first() as IDataSourceDetailInfo;

      if (RefreshPropertyHelper.canBeRefreshedRemotely(datasourceDetailInfo)) {
        scope.dialog.showModeSelector = !(allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient);
        scope.dialog.showRefreshOnClientTab = true;

        scope.selfServiceSchedulePaneProps =
          CreateBridgeExtractTaskActionService.getDefaultSelfServiceSchedulePanePropsForSSSWidget(datasourceDetailInfo.remoteAgentTimeZone);

        scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback = (newRecurrence: Recurrence) => {
          scope.$apply(() => {
            scope.selfServiceSchedulePaneProps = {
              recurrenceModel: newRecurrence,
              timeZoneDetails: scope.selfServiceSchedulePaneProps.timeZoneDetails,
              recurrenceModelChangedCallback: scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback,
              type: scope.selfServiceSchedulePaneProps.type,
            };
          });
        };

        scope.remoteRefreshPropertiesEditModel = { refreshMode: datasourceDetailInfo.refreshMode, remoteAgentName: datasourceDetailInfo.remoteAgentName };
        CreateBridgeExtractTaskActionService.setRemoteAgentOptionsInScope(datasourceDetailInfo, scope);

        if (scope.remoteRefreshPropertiesEditModel.refreshMode === 'Remote' || allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient) {
          scope.dialog.mode = DialogMode.ScheduleRefreshOnClient;
        }
      }
    }

    scope.existingRemoteRefreshPropertiesEditModel = {
      refreshMode: scope.remoteRefreshPropertiesEditModel.refreshMode,
      remoteAgentName: scope.remoteRefreshPropertiesEditModel.remoteAgentName
    };
  }

  export async function execute<T extends IExtractItemInfo & IContentInfo>(
    items: Set<T>,
    context: IActionContext,
    scheduleOnServerPrompt: string,
    scheduleOnClientPrompt: string,
    scheduleRefreshActionOnServer: (ids: string[], scheduleId: string, type: string) => Promise<IResult>,
    scheduleRefreshActionOnClient: (scope: ICreateExtractTasksScope, id: string, schedule: IRemoteRefreshScheduleInfo) => Promise<IResult>,
    scheduleRefreshActionOnClientReact: (refreshType: string, recurrence: Recurrence, remoteAgentName: string) => Promise<IResult>,
    refreshNowPrompt: string,
    refreshNowAction: (ids: string[], type: string) => Promise<IResult>,
    errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<T>) => IErrorDetail,
    allowedMode: CreateExtractTasksMode
  ): Promise<IResult> {

    if (FeatureFlags.schedulesARMEnabled) {
      // The legacy logic uses the schedule for some rare error messages, only for the recommended tab
      // As such we do something similar by exposing the schedule to the error function if needed
      let scheduleForError = undefined as ISchedule;
      const scheduleFn = (type: RefreshTaskType, schedule: ISchedule) => {
        const itemIds = items.keys();
        scheduleForError = schedule;
        return scheduleRefreshActionOnServer(itemIds, schedule.id, type);
      };
      const runNowFn = (type: RefreshTaskType) => {
        const itemIds = items.keys();
        return refreshNowAction(itemIds, type);
      };
      const useDescriptionAsName = SessionServerSelectors.displayScheduleDescriptionAsName(getAppStore().getState());

      // Bridge has a lot of bridge specific logic
      const allowBridge = (allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient ||
        allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient ||
        allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient)
        && items.size() === 1;
      const firstItem = items.first();
      const remoteAgentTimeZone = allowBridge && 'remoteAgentTimeZone' in firstItem
        ? (firstItem as undefined as IDataSourceDetailInfo).remoteAgentTimeZone
        : null;
      // Method actually accepts null TZ name, contrary to what it declares
      const sssPaneProps = CreateBridgeExtractTaskActionService.getDefaultSelfServiceSchedulePanePropsForSSSWidget(remoteAgentTimeZone);
      const existingRefreshSchedulesCount = selectContentTotalLength(getAppStore().getState());
      const datasources: IDataSourceDetailInfo[] = allowBridge
        ? items.toArray().filter(c => c.contentType === 'datasource').map(d => d as undefined as IDataSourceDetailInfo)
        : [];
      const showExistingTasksWillBeDeleteWarning = allowBridge && existingRefreshSchedulesCount > 0 && RefreshPropertyHelper.allDatasourcesHaveLegacyBridgeSchedules(datasources);

      const remoteAgentOptions = allowBridge
        ? await CreateBridgeExtractTaskActionService.getRemoteAgentOptions(firstItem as undefined as IDataSourceDetailInfo)
        : [];
      const scheduleBridgeExtract = (recurrence: Recurrence, remoteAgentName: string, refreshType: string) => {
        return scheduleRefreshActionOnClientReact(refreshType, recurrence, remoteAgentName);
      };

      // Technically this means the user can think they're scheduling everything as incremental when that would be impossible
      // but that's current behavior so it stays
      const allowIncremental = _.any(items.toArray(), (item: IExtractItemInfo) => item.hasIncrementalExtract);
      const allowRunNow = allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServer
        || allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient;
      const allowRecommended = allowedMode === CreateExtractTasksMode.AllowScheduleOnServer
        || allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient
        || allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServer
        || allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient;
      const dialogProps: Omit<PropsForCreateExtractTaskActionBody, 'close'> = {
        renderRunNowTab: allowRunNow,
        renderRecommendedTab: allowRecommended,
        renderBridgeTab: allowBridge,
        schedule: scheduleFn,
        useDescriptionAsName,
        runNow: runNowFn,
        disableIncremental: !allowIncremental,
        runNowTabPrompt: refreshNowPrompt,
        description: scheduleOnServerPrompt,
        remoteAgentOptions,
        showExistingTasksWillBeDeleteWarning,
        scheduleBridgeExtract,
        recurrence: sssPaneProps.recurrenceModel,
        scheduleOnClientPrompt,
      };
      const dialog = openCreateExtractTaskActionDialog(dialogProps);
      const result = dialog.result.then(result => {
        if (result) {
          return ActionHelpers.displayStandardToastsForResult(
            items,
            result,
            {
              success: standardActionSuccessMsg(Messages.extractTasksCreateSuccess),
              error: standardActionErrorMsg(Messages.extractTasksCreateError),
            },
            (errorItem: IErrorItem<T>) => errorDetailFn(scheduleForError)(errorItem)
          );
        }
      });
      return toNativePromise(result);
    }

    const scope = <ICreateExtractTasksScope>context.scope.$new();
    populateScope(scope, items, scheduleOnServerPrompt, scheduleOnClientPrompt, allowedMode, refreshNowPrompt);

    // Change the available buttons based on the dialog mode
    const buttons: IConfirmActionDialogButton[] = [];
    scope.$watch(() => scope.dialog.mode, () => {
      buttons.length = 0;
      if (scope.dialog.mode === DialogMode.ScheduleRefreshOnServer) {
        scope.remoteRefreshPropertiesEditModel.refreshMode = RefreshModeTypes.Server;

        buttons.push({
          label: items.size() === 1 ? Messages.extractTaskCreateConfirm() : Messages.extractTasksCreateConfirm(),
          action: () => scheduleRefreshActionOnServer(items.keys(), scope.extractTask.schedule.id, scope.extractTask.type),
          disabled: () => !scope.extractTask.schedule || !scope.extractTask.type
        });
      } else if (scope.dialog.mode === DialogMode.ScheduleRefreshOnClient) {

        const recurrenceModel: Recurrence = scope.selfServiceSchedulePaneProps.recurrenceModel;
        const schedule: IRemoteRefreshScheduleInfo = {
          id: '1',
          contentType: 'remoteRefreshSchedule',
          definition: null,
          refreshType: scope.remoteRefreshTypeOption.refreshType,
          scheduleDetails: recurrenceModel.recurrenceDetails,
          scheduleType: recurrenceModel.recurrenceType
        };

        scope.remoteRefreshPropertiesEditModel.refreshMode = RefreshModeTypes.Remote;

        const buttonLabel = CreateExtractTasksMode.AllowScheduleOnlyOnClient
          ? Messages.extractTaskCreateLegacyBridgeScheduleConfirm()
          : Messages.extractTaskCreateConfirm();
        buttons.push({
          label: buttonLabel,
          action: () => scheduleRefreshActionOnClient(scope, '1', schedule),
          disabled: () => !scope.remoteAgentOptions ||
            scope.remoteAgentOptions.length === 0 ||
            !scope.remoteRefreshPropertiesEditModel ||
            !scope.remoteRefreshPropertiesEditModel.remoteAgentName
        });
      } else if (scope.dialog.mode === DialogMode.RefreshNow) {
        buttons.push({
          label: Messages.fullRefresh(),
          action: () => refreshNowAction(items.keys(), ExtractTaskType.RefreshExtract),
          disabled: () => !hasRunNowPermission(getAppStore().getState()),
        });
        if (scope.allowIncremental) {
          buttons.push({
            label: Messages.incrementalRefresh(),
            action: () => refreshNowAction(items.keys(), ExtractTaskType.IncrementExtract),
            disabled: () => !hasRunNowPermission(getAppStore().getState()),
          });
        }
      } else {
        throw 'Unknown dialog mode: ' + scope.dialog.mode;
      }
    });

    scope.itemTextFn = (schedule: ISchedule) => {
      const description = IScheduleInfo.getScheduleDescription(schedule);
      if (!schedule.parallel) {
        return Messages.serialScheduleDescription({
          SERIAL_DESCRIPTION: description,
          SERIAL_DESCRIPTION_SUFFIX: Messages.serialScheduleDescriptionSuffix(),
        });
      }
      return description;
    };

    scope.displayScheduleDescriptionAsName = getSessionManagementServiceInstance().displayScheduleDescriptionAsName();

    const angularConfirmDialog = ServiceLocator.get<ConfirmActionDialog>('ConfirmActionDialog');
    const dialogInstance = angularConfirmDialog.open({
      scope: scope,
      position: { reserveHeight: 550 },
      bodyTemplateUrl: 'confirmActionDialogExtractRefresh.html',
      title: CreateExtractTaskActionService.dialogTitle(allowedMode),
      buttons: buttons,
      size: ModalSize.large
    });

    dialogInstance.result
      .then(result =>
        ActionHelpers.displayStandardToastsForResult(
          items,
          result,
          {
            success: standardActionSuccessMsg(Messages.extractTasksCreateSuccess),
            error: standardActionErrorMsg(Messages.extractTasksCreateError),
          },
          (errorItem: IErrorItem<T>) => errorDetailFn(scope.extractTask.schedule)(errorItem)
        )
      ).finally(() => scope.$destroy());

    return toNativePromise(dialogInstance.result);
  }
}